import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { C2bBillCandidate, C2bPaymentView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  EmptyState,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  SegmentedControl,
  Table,
  TableBody,
  TableHeader,
  TextArea,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useState } from 'react';

import { billingKeys } from '../api/billing.ts';
import {
  lookUpBill,
  matchPaybillPayment,
  paybillKeys,
  paybillPaymentsQuery,
  type PaybillStatus,
} from '../api/paybill.ts';
import { can, sessionQuery } from '../api/session.ts';
import styles from '../billing/billing.module.css';
import { cents, money } from '../billing/format.ts';
import { useOnline } from '../hooks/use-online.ts';
import { outsideDialogs, ShortcutHint } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';

// Paybill payments (ADR 0020; cashier-and-payments.md §2.5). Money patients sent straight
// to the facility's paybill. Wodi puts each on the bill its account number names; the rest
// wait here until someone with permission matches them, with a reason. Nothing here
// refunds money: an extra payment becomes credit on the visit, refunded with approval.

const VIEWS = [
  { id: 'unmatched', label: 'Waiting' },
  { id: 'matched', label: 'Matched' },
] as const;

const EMPTY: Record<PaybillStatus, string> = {
  unmatched:
    'No paybill payments are waiting. Payments whose account number names a bill go on it by themselves.',
  matched: 'No paybill payments have been matched yet.',
};

export function PaybillPaymentsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const mayMatch = session !== null && can(session, 'payments.c2b.match');
  const online = useOnline();
  const [status, setStatus] = useState<PaybillStatus>('unmatched');
  const [matching, setMatching] = useState<C2bPaymentView | null>(null);
  const list = useQuery({ ...paybillPaymentsQuery(status), refetchInterval: 15_000 });
  const oldestWaiting =
    status === 'unmatched' && list.isSuccess ? list.data.items.at(-1) : undefined;
  // M matches the oldest waiting payment, as N opens the oldest bill at the cashier.
  useShortcut(
    'm',
    outsideDialogs(() => {
      if (oldestWaiting !== undefined) setMatching(oldestWaiting);
    }),
    {
      enabled: mayMatch && online && oldestWaiting !== undefined,
      description: 'Match the oldest waiting paybill payment',
    },
  );
  return (
    <div className={styles.page}>
      <PageHeader
        title="Paybill payments"
        actions={<Link to="/cashier">Back to the cashier</Link>}
      />
      <p>
        Payments made straight to the facility&apos;s paybill. Each one goes on the bill whose visit
        or invoice number the patient typed as the account number. The rest wait here.
      </p>
      {!online && list.isSuccess ? (
        <Notice tone="info">{`Offline, showing payments loaded at ${formatInstant(new Date(list.dataUpdatedAt).toISOString())}. Matching needs a connection.`}</Notice>
      ) : null}
      <Section
        title={status === 'unmatched' ? 'Waiting to be matched' : 'Matched'}
        {...(list.isSuccess
          ? {
              description:
                list.data.unmatchedCount === 1
                  ? '1 payment is waiting.'
                  : `${String(list.data.unmatchedCount)} payments are waiting.`,
            }
          : {})}
        actions={
          <SegmentedControl
            aria-label="Which payments"
            options={VIEWS}
            value={status}
            onChange={(id) => {
              setStatus(id as PaybillStatus);
            }}
          />
        }
      >
        {list.isPending ? <LoadingBlock label="Loading paybill payments" rows={3} /> : null}
        {list.isError ? (
          <Notice
            tone="error"
            action={
              <Button dense onPress={() => void list.refetch()}>
                Try again
              </Button>
            }
          >
            {errorText(list.error, "Paybill payments couldn't be loaded. Try again.")}
          </Notice>
        ) : null}
        {list.isSuccess && list.data.items.length === 0 ? (
          <EmptyState>{EMPTY[status]}</EmptyState>
        ) : null}
        {list.isSuccess && list.data.items.length > 0 ? (
          <PaymentsTable
            items={list.data.items}
            status={status}
            mayMatch={mayMatch}
            online={online}
            onMatch={setMatching}
          />
        ) : null}
        {!mayMatch && status === 'unmatched' && list.isSuccess && list.data.items.length > 0 ? (
          <p className={styles.meta}>
            Matching a payment to a bill needs the permission to match paybill payments. Ask a
            finance approver.
          </p>
        ) : null}
        {mayMatch && oldestWaiting !== undefined ? (
          <ShortcutHint combo="m" action="match the oldest waiting payment" />
        ) : null}
      </Section>
      {matching === null ? null : (
        <MatchDialog
          payment={matching}
          onClose={() => {
            setMatching(null);
          }}
        />
      )}
    </div>
  );
}

function PaymentsTable({
  items,
  status,
  mayMatch,
  online,
  onMatch,
}: {
  items: C2bPaymentView[];
  status: PaybillStatus;
  mayMatch: boolean;
  online: boolean;
  onMatch: (payment: C2bPaymentView) => void;
}) {
  return (
    <Table
      aria-label={status === 'unmatched' ? 'Paybill payments waiting' : 'Paybill payments matched'}
      flat
    >
      <TableHeader>
        <Column isRowHeader>M-Pesa code</Column>
        <Column>Paid</Column>
        <Column numeric>Amount</Column>
        <Column>Account number typed</Column>
        <Column>{status === 'unmatched' ? 'Why it is waiting' : 'Bill'}</Column>
        <Column>
          <span className={styles.meta}>{status === 'unmatched' ? 'Action' : 'Matched by'}</span>
        </Column>
      </TableHeader>
      <TableBody items={items}>
        {(p) => (
          <Row id={p.id}>
            <Cell>
              <span className="wodi-id">{p.transactionCode}</span>
            </Cell>
            <Cell>{formatInstant(p.paidAt)}</Cell>
            <Cell numeric>{money(p.amount)}</Cell>
            <Cell>
              {p.accountReference.trim() === '' ? (
                'Nothing typed'
              ) : (
                <span className="wodi-id">{p.accountReference}</span>
              )}
            </Cell>
            <Cell>
              {p.match === null ? (
                (p.unmatchedReasonText ?? '')
              ) : (
                <span>
                  <span>Visit </span>
                  <Link to="/cashier" search={{ visitId: p.match.visitId }}>
                    <span className="wodi-id">{p.match.visitNumber}</span>
                  </Link>
                  {p.match.receiptNumber === null ? null : (
                    <>
                      <span>, receipt </span>
                      <span className="wodi-id">{p.match.receiptNumber}</span>
                    </>
                  )}
                </span>
              )}
            </Cell>
            <Cell>
              {p.match !== null ? (
                <span>
                  {p.match.method === 'auto'
                    ? 'Matched automatically'
                    : `${p.match.matchedByName ?? 'Someone'}: ${p.match.reason ?? ''}`}
                </span>
              ) : mayMatch ? (
                <Button
                  dense
                  isDisabled={!online}
                  aria-label={`Match payment ${p.transactionCode} to a bill`}
                  onPress={() => {
                    onMatch(p);
                  }}
                >
                  Match to bill
                </Button>
              ) : null}
            </Cell>
          </Row>
        )}
      </TableBody>
    </Table>
  );
}

/** What happens to the money on this bill, in words, before the person confirms. */
function outcomeSentence(payment: C2bPaymentView, bill: C2bBillCandidate): string {
  const paid = cents(payment.amount);
  const owed = cents(bill.owed);
  if (owed === 0n)
    return `This bill owes nothing, so all ${money(payment.amount)} will be credit on the visit. A refund needs approval.`;
  if (paid > owed)
    return `It pays the ${money(bill.owed)} owed. The other ${money({ currency: 'KES', cents: (paid - owed).toString() })} will be credit on the visit.`;
  if (paid === owed) return `It pays the bill in full.`;
  return `It pays ${money(payment.amount)} of the ${money(bill.owed)} owed, oldest charges first.`;
}

function MatchDialog({ payment, onClose }: { payment: C2bPaymentView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const [number, setNumber] = useState('');
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [bill, setBill] = useState<C2bBillCandidate | null>(null);
  const [searched, setSearched] = useState(false);
  const find = useMutation({
    mutationFn: () => lookUpBill(number),
    onSuccess: (result) => {
      setSearched(true);
      setBill(result.bill);
    },
  });
  const match = useMutation({
    mutationFn: (visitId: string) => matchPaybillPayment(payment.id, visitId, reason.trim()),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: paybillKeys.all });
      await queryClient.invalidateQueries({ queryKey: billingKeys.all });
      toasts.show({
        title: 'Payment matched',
        ...(result.payment.match?.receiptNumber
          ? { description: `Receipt ${result.payment.match.receiptNumber}` }
          : {}),
      });
      onClose();
    },
  });
  const cancelled = bill?.visitStatus === 'cancelled';
  const shortReason = reason.trim().length < 3;
  const confirm = () => {
    setTried(true);
    if (bill === null || cancelled || shortReason || !online) return;
    match.mutate(bill.visitId);
  };
  useShortcut('mod+enter', confirm, { allowInFields: true, description: 'Match payment' });
  return (
    <Dialog
      title="Match payment to a bill"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <p>
          <span className="wodi-id">{payment.transactionCode}</span>
          <span>{`, ${money(payment.amount)}, paid ${formatInstant(payment.paidAt)}. Typed as the account number: `}</span>
          <span className="wodi-id">
            {payment.accountReference.trim() === '' ? 'nothing' : payment.accountReference}
          </span>
          <span>.</span>
        </p>
        <form
          className={styles.row}
          onSubmit={(event) => {
            event.preventDefault();
            if (number.trim() !== '') find.mutate();
          }}
        >
          <TextField
            label="Visit or invoice number"
            description="As printed on the bill or the patient's receipt."
            value={number}
            onChange={(value) => {
              setNumber(value);
              setSearched(false);
              setBill(null);
            }}
            width="md"
            autoFocus
          />
          <Button
            type="submit"
            isPending={find.isPending}
            pendingLabel="Finding"
            isDisabled={!online}
          >
            Find bill
          </Button>
        </form>
        {find.isError ? (
          <Notice tone="error">
            {errorText(find.error, "The bill couldn't be looked up. Try again.")}
          </Notice>
        ) : null}
        {searched && bill === null ? (
          <Notice tone="info">
            No bill here has that number. Check the number on the patient&apos;s bill or receipt.
          </Notice>
        ) : null}
        {bill === null ? null : (
          <section className={styles.request} aria-label="Bill found">
            <p>
              <strong>{bill.patient?.name ?? 'Counter sale'}</strong>
              {bill.patient === null ? null : (
                <span className="wodi-id">{` ${bill.patient.patientNumber}`}</span>
              )}
            </p>
            <p className={styles.meta}>
              <span>Visit </span>
              <span className="wodi-id">{bill.visitNumber}</span>
              <span>{`, ${formatInstant(bill.startedAt)}. Owes ${money(bill.owed)}.`}</span>
            </p>
            {cancelled ? (
              <Notice tone="error">
                This visit was cancelled, so no money can go on its bill. Find another bill.
              </Notice>
            ) : (
              <p>{outcomeSentence(payment, bill)}</p>
            )}
          </section>
        )}
        <TextArea
          label="Reason"
          description="Why this payment belongs to this bill. Stored and audited."
          value={reason}
          onChange={setReason}
          {...(tried && shortReason ? { errorMessage: 'Say why, in a few words.' } : {})}
        />
        {tried && bill === null ? <Notice tone="error">Find the bill first.</Notice> : null}
        {match.isError ? (
          <Notice tone="error">
            {errorText(match.error, "The payment wasn't matched. Try again.")}
          </Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onPress={confirm}
          isPending={match.isPending}
          pendingLabel="Matching"
          isDisabled={!online || cancelled}
        >
          Match payment
        </Button>
      </DialogActions>
    </Dialog>
  );
}
